import { useState } from 'react';
import { toast } from 'sonner';
import { api, ApiError } from '../../lib/api';
import { formatDateTime } from '../../lib/dateFmt';
import {
  AlertOctagon,
  AlertTriangle,
  Boxes,
  Camera,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  DoorOpen,
  FileSignature,
  FileText,
  GraduationCap,
  History,
  Archive,
  ArchiveRestore,
  KeyRound,
  LogIn,
  LogOut,
  Mail,
  MessageSquare,
  PackageX,
  RefreshCcw,
  Send,
  ShieldCheck,
  ClipboardList,
  type LucideIcon,
} from 'lucide-react';
import type {
  ArrivalUndonePayload,
  CertificateSentPayload,
  EventType,
  DocumentAttachedPayload,
  IssuedKitItem,
  KitIssuedPayload,
  KitReceiptIssuedPayload,
  KitReturnedPayload,
  LeftEarlyPayload,
  MobilisationEvent,
  PersonArrivedPayload,
  FlagRaisedPayload,
  FlagClearedPayload,
  TrainingAttemptCompletedPayload,
} from '@hcc/types';
import { SkeletonList } from '../ui/Skeleton';
import { CollapsibleSection } from '../ui/CollapsibleSection';
import { SignatureModal } from './SignatureModal';

/**
 * Human-readable timeline for a person's event chain. Replaces the
 * raw-JSON list that previously rendered on /admin/people/:id —
 * coordinators want to *read* the audit trail at a glance ("arrived
 * 09:04, PPE issued, flag raised"), not parse JSON. Each row keeps
 * a small "raw" toggle for the admin who occasionally wants to see
 * the payload that was hashed into the chain.
 */
export function EventTimeline({
  events,
  personId,
}: {
  events: MobilisationEvent[] | null;
  /** When set, CertificateSent rows show a "View certificate" button. */
  personId?: string;
}) {
  const count = events?.length ?? 0;
  return (
    <CollapsibleSection
      icon={<History className="h-4 w-4 text-fg-muted" />}
      title="Event history"
      trailing={
        events ? `${count} entr${count === 1 ? 'y' : 'ies'}` : undefined
      }
    >
      <p className="text-xs text-fg-muted">
        Append-only chain. Every row is part of the SHA-256 audit
        trail; rows can&apos;t be edited, only added to.
      </p>
      {events === null ? (
        <div className="mt-4">
          <SkeletonList rows={3} />
        </div>
      ) : events.length === 0 ? (
        <p className="mt-4 text-sm text-fg-muted">No events yet.</p>
      ) : (
        <ol className="mt-4 space-y-2">
          {events.map((ev) => (
            <EventRow key={ev.id} ev={ev} personId={personId} />
          ))}
        </ol>
      )}
    </CollapsibleSection>
  );
}

// Tones map onto the four soft-status tokens — each event type picks
// one. The visual variety (success / warning / danger / accent /
// neutral) carries the same meaning in light, dark, and cb themes.
const TONE_CLASSES = {
  success: 'bg-success-soft/15 text-success-soft-fg ring-success-soft/25',
  warning: 'bg-warning-soft/15 text-warning-soft-fg ring-warning-soft/25',
  danger: 'bg-danger-soft/15 text-danger-soft-fg ring-danger-soft/25',
  info: 'bg-info-soft/15 text-info-soft-fg ring-info-soft/25',
  accent: 'bg-accent/10 text-accent ring-accent/25',
  neutral: 'bg-neutral-soft/10 text-fg-muted ring-line/15',
} as const;

type Tone = keyof typeof TONE_CLASSES;

const TYPE_SPEC: Record<EventType, { icon: LucideIcon; tone: Tone }> = {
  PersonArrived: { icon: LogIn, tone: 'success' },
  ArrivalUndone: { icon: LogOut, tone: 'neutral' },
  PhotoCaptured: { icon: Camera, tone: 'accent' },
  KitIssued: { icon: Boxes, tone: 'info' },
  KitReturned: { icon: PackageX, tone: 'warning' },
  FlagRaised: { icon: AlertOctagon, tone: 'danger' },
  FlagCleared: { icon: ShieldCheck, tone: 'success' },
  NoteAdded: { icon: MessageSquare, tone: 'info' },
  NoteRemoved: { icon: MessageSquare, tone: 'neutral' },
  DocumentAttached: { icon: FileSignature, tone: 'accent' },
  LeftEarly: { icon: DoorOpen, tone: 'warning' },
  LeftEarlyUndone: { icon: LogIn, tone: 'success' },
  CertificateSent: { icon: Mail, tone: 'success' },
  KitReceiptIssued: { icon: FileText, tone: 'info' },
  SapEmployeePushed: { icon: Send, tone: 'success' },
  PlanonPersonCreated: { icon: Send, tone: 'success' },
  TaskAssigned: { icon: ClipboardList, tone: 'neutral' },
  TaskUpdated: { icon: ClipboardList, tone: 'neutral' },
  TaskSubmitted: { icon: CheckCircle2, tone: 'warning' },
  TaskApproved: { icon: ShieldCheck, tone: 'success' },
  TaskRejected: { icon: AlertOctagon, tone: 'danger' },
  TaskRemoved: { icon: PackageX, tone: 'neutral' },
  CheckProcessed: { icon: ShieldCheck, tone: 'info' },
  OnboardingDataCaptured: { icon: ClipboardList, tone: 'neutral' },
  OnboardingSubmissionAmended: { icon: FileSignature, tone: 'warning' },
  DocumentAcknowledged: { icon: ShieldCheck, tone: 'success' },
  TrainingAttemptCompleted: { icon: GraduationCap, tone: 'info' },
  TrainingCertificateIssued: { icon: Mail, tone: 'success' },
  TrainingAnswersNotified: { icon: Mail, tone: 'info' },
  KitFormSubmitted: { icon: ClipboardList, tone: 'info' },
  PersonArchived: { icon: Archive, tone: 'warning' },
  PersonUnarchived: { icon: ArchiveRestore, tone: 'success' },
  PersonEmailChanged: { icon: KeyRound, tone: 'warning' },
};

function EventRow({
  ev,
  personId,
}: {
  ev: MobilisationEvent;
  personId?: string;
}) {
  const [raw, setRaw] = useState(false);
  const [sigOpen, setSigOpen] = useState(false);
  const spec = TYPE_SPEC[ev.event_type] ?? {
    icon: History,
    tone: 'neutral' as const,
  };
  const Icon = spec.icon;
  const toneClass = TONE_CLASSES[spec.tone];
  const when = new Date(ev.created_at);
  const items =
    ev.event_type === 'KitIssued' || ev.event_type === 'KitReturned'
      ? ((ev.payload as KitIssuedPayload | KitReturnedPayload).items ?? [])
      : undefined;
  const sigSubtitle =
    ev.event_type === 'KitIssued' || ev.event_type === 'KitReturned'
      ? `${(ev.payload as KitIssuedPayload | KitReturnedPayload).station} station`
      : undefined;

  return (
    <li className="rounded-token-sm border border-line/10 bg-surface">
      <div className="flex items-start gap-3 p-3">
        <span
          aria-hidden
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-token-sm ring-1 ${toneClass}`}
        >
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="text-sm font-medium text-fg">
              {summaryFor(ev)}
            </span>
            {ev.signature_s3_key ? (
              <span className="inline-flex items-center gap-1 rounded-token-sm bg-success-soft/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-success-soft-fg">
                <CheckCircle2 className="h-2.5 w-2.5" />
                Signed
              </span>
            ) : null}
          </div>
          {detailFor(ev) ? (
            <div className="mt-0.5 text-xs text-fg-muted">{detailFor(ev)}</div>
          ) : null}
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-fg-dim">
            <span
              title={when.toISOString()}
              className="tabular-nums"
            >
              {formatDateTime(when)}
            </span>
            <button
              type="button"
              onClick={() => setRaw((v) => !v)}
              className="inline-flex items-center gap-0.5 rounded text-[11px] text-fg-dim transition-colors hover:text-fg"
              aria-expanded={raw}
            >
              {raw ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
              Raw payload
            </button>
            {ev.signature_s3_key ? (
              <button
                type="button"
                onClick={() => setSigOpen(true)}
                className="inline-flex items-center gap-0.5 rounded text-[11px] text-success-soft-fg transition-colors hover:underline"
              >
                <FileSignature className="h-3 w-3" />
                View signature
              </button>
            ) : null}
            {ev.event_type === 'CertificateSent' && personId ? (
              <a
                href={`/api/admin/certificates/${personId}/pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-0.5 rounded text-[11px] text-success-soft-fg transition-colors hover:underline"
              >
                <FileSignature className="h-3 w-3" />
                View certificate
              </a>
            ) : null}
            {ev.event_type === 'KitIssued' && personId ? (
              <>
                <a
                  href={`/api/admin/kit-receipts/${ev.id}/pdf?person_id=${personId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-0.5 rounded text-[11px] text-accent transition-colors hover:underline"
                >
                  <FileText className="h-3 w-3" />
                  View receipt
                </a>
                <RegenerateReceiptButton personId={personId} eventId={ev.id} />
              </>
            ) : null}
            {ev.event_type === 'KitFormSubmitted' &&
            (ev.payload as { submission_id?: string }).submission_id ? (
              <a
                href={`/api/kit-handover/submissions/${(ev.payload as { submission_id: string }).submission_id}/pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-0.5 rounded text-[11px] text-accent transition-colors hover:underline"
              >
                <FileText className="h-3 w-3" />
                View checklist PDF
              </a>
            ) : null}
            <span className="font-mono text-[10px] text-fg-dim" title={ev.chain_hash}>
              chain: {ev.chain_hash.slice(0, 12)}…
            </span>
          </div>
          {raw ? (
            <pre className="mt-2 overflow-x-auto rounded-token-sm bg-surface-2 p-2 font-mono text-[11px] leading-relaxed text-fg">
              {JSON.stringify(ev.payload, null, 2)}
            </pre>
          ) : null}
        </div>
      </div>
      {sigOpen && ev.signature_s3_key ? (
        <SignatureModal
          signatureKey={ev.signature_s3_key}
          title={summaryFor(ev)}
          whenISO={ev.created_at}
          items={items}
          subtitle={sigSubtitle}
          onClose={() => setSigOpen(false)}
        />
      ) : null}
    </li>
  );
}

/**
 * One-click "regenerate the receipt PDF" from a KitIssued row. Works
 * for legacy events (creates the receipt for the first time) and for
 * issuances that already have a receipt (overwrites the stored PDF
 * with one rendered from the current template). Never re-emails.
 */
function RegenerateReceiptButton({
  personId,
  eventId,
}: {
  personId: string;
  eventId: string;
}) {
  const [busy, setBusy] = useState(false);
  async function go() {
    setBusy(true);
    try {
      await api(`/api/admin/kit-receipts/${eventId}/regenerate`, {
        method: 'POST',
        body: JSON.stringify({ person_id: personId }),
      });
      toast.success(
        'Receipt re-rendering. Refresh in a moment, then click View receipt.',
      );
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'regenerate failed');
    } finally {
      setBusy(false);
    }
  }
  return (
    <button
      type="button"
      onClick={go}
      disabled={busy}
      className="inline-flex items-center gap-0.5 rounded text-[11px] text-accent transition-colors hover:underline disabled:opacity-50"
      title="Re-render this receipt against the current template. Does not re-email."
    >
      <RefreshCcw className={`h-3 w-3 ${busy ? 'animate-spin' : ''}`} />
      {busy ? 'Working…' : 'Regenerate receipt'}
    </button>
  );
}

function summaryFor(ev: MobilisationEvent): string {
  switch (ev.event_type) {
    case 'PersonArrived': {
      const p = ev.payload as PersonArrivedPayload;
      return p.via === 'self_check_in' ? 'Self-checked in' : 'Checked in at reception';
    }
    case 'ArrivalUndone':
      return 'Check-in undone';
    case 'PhotoCaptured':
      return 'Reception photo captured';
    case 'KitIssued': {
      const p = ev.payload as KitIssuedPayload;
      const n = p.items?.length ?? 0;
      return `${p.station} kit issued — ${n} item${n === 1 ? '' : 's'}`;
    }
    case 'KitReturned': {
      const p = ev.payload as KitReturnedPayload;
      const n = p.items?.length ?? 0;
      return `${p.station} kit returned — ${n} item${n === 1 ? '' : 's'}`;
    }
    case 'FlagRaised':
      return 'Flag raised';
    case 'FlagCleared':
      return 'Flag cleared';
    case 'NoteAdded': {
      const p = ev.payload as { text?: string; station?: string };
      return p.station ? `Note added · from ${p.station}` : 'Note added';
    }
    case 'NoteRemoved':
      return 'Note removed';
    case 'DocumentAttached': {
      const p = ev.payload as DocumentAttachedPayload;
      return `Document attached — ${p.name}`;
    }
    case 'LeftEarly':
      return 'Left early';
    case 'LeftEarlyUndone':
      return 'Came back on site';
    case 'CertificateSent': {
      const p = ev.payload as CertificateSentPayload;
      const tag =
        p.via === 'auto'
          ? 'auto-sent on completion'
          : p.via === 'bulk'
            ? 'bulk send'
            : 're-send';
      return `Certificate emailed — ${tag}`;
    }
    case 'KitReceiptIssued': {
      const p = ev.payload as KitReceiptIssuedPayload;
      return p.emailed
        ? `${p.station} receipt emailed`
        : `${p.station} receipt stored`;
    }
    case 'SapEmployeePushed': {
      const p = ev.payload as { sap_employee_id?: string };
      return `Pushed to SAP — employee ${p.sap_employee_id ?? ''}`.trim();
    }
    case 'PlanonPersonCreated': {
      const p = ev.payload as { planon_syscode?: number };
      return `Created in Planon — syscode ${p.planon_syscode ?? ''}`.trim();
    }
    case 'TrainingAttemptCompleted': {
      const p = ev.payload as TrainingAttemptCompletedPayload;
      return p.passed
        ? `Training passed — ${p.score}%`
        : `Training attempt — ${p.score}% (not passed)`;
    }
    case 'TrainingCertificateIssued':
      return 'Training certificate issued';
    case 'TrainingAnswersNotified': {
      const p = ev.payload as { to?: string[] };
      const count = p.to?.length ?? 0;
      return `Answers emailed to ${count} recipient${count === 1 ? '' : 's'}`;
    }
    case 'KitFormSubmitted': {
      const p = ev.payload as { form_title?: string; station?: string };
      return `${p.station ?? ''} handover checklist signed${p.form_title ? ` — ${p.form_title}` : ''}`.trim();
    }
    case 'OnboardingSubmissionAmended': {
      const p = ev.payload as { changed_keys?: string[] };
      const n = p.changed_keys?.length ?? 0;
      return `Form answers amended by staff — ${n} field${n === 1 ? '' : 's'}`;
    }
    case 'PersonArchived':
      return 'Archived (hidden from live views)';
    case 'PersonUnarchived':
      return 'Unarchived';
    case 'PersonEmailChanged': {
      const p = ev.payload as { from?: string; to?: string };
      return `Work email changed — ${p.from ?? '?'} to ${p.to ?? '?'}`;
    }
    default:
      return ev.event_type;
  }
}

function detailFor(ev: MobilisationEvent): string | null {
  switch (ev.event_type) {
    case 'OnboardingSubmissionAmended': {
      const p = ev.payload as { changed_keys?: string[] };
      return (p.changed_keys ?? []).join(' · ') || null;
    }
    case 'KitIssued':
    case 'KitReturned': {
      const p = ev.payload as KitIssuedPayload | KitReturnedPayload;
      const parts = (p.items ?? []).map(describeItem).filter(Boolean);
      const items = parts.slice(0, 4).join(' · ');
      const overflow = parts.length > 4 ? ` (+${parts.length - 4} more)` : '';
      const note = p.notes ? ` — ${p.notes}` : '';
      return `${items}${overflow}${note}` || null;
    }
    case 'FlagRaised': {
      const p = ev.payload as FlagRaisedPayload;
      return p.reason || null;
    }
    case 'FlagCleared': {
      const p = ev.payload as FlagClearedPayload;
      return p.resolution || null;
    }
    case 'LeftEarly': {
      const p = ev.payload as LeftEarlyPayload;
      const back = p.returning ? ' (expected back)' : '';
      return `${p.reason}${back}`;
    }
    case 'DocumentAttached': {
      const p = ev.payload as DocumentAttachedPayload;
      return p.description || null;
    }
    case 'ArrivalUndone': {
      const p = ev.payload as ArrivalUndonePayload;
      return p.reason || null;
    }
    case 'CertificateSent': {
      const p = ev.payload as CertificateSentPayload;
      return p.to;
    }
    case 'KitReceiptIssued': {
      const p = ev.payload as KitReceiptIssuedPayload;
      return p.to ?? null;
    }
    case 'PersonEmailChanged': {
      const p = ev.payload as { reason?: string; account_updated?: boolean };
      const account = p.account_updated
        ? 'sign-in account moved with it'
        : 'no sign-in account to move';
      return p.reason ? `${p.reason} (${account})` : account;
    }
    case 'PersonArrived':
    default:
      return null;
  }
}

function describeItem(it: IssuedKitItem): string {
  const bits: string[] = [it.name];
  if (it.quantity && it.quantity > 1) bits.push(`×${it.quantity}`);
  if (it.size) bits.push(`size ${it.size}`);
  if (it.registration) bits.push(it.registration);
  else if (it.serial) bits.push(`SN ${it.serial}`);
  return bits.join(' ');
}

function titleCase(slug: string): string {
  if (!slug) return '';
  return slug
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase());
}
